//#region src/errors.ts
/**
* The single error type thrown across the entire Rebase client surface —
* HTTP data/control-plane calls, realtime/WebSocket operations, and
* client-side logic errors (e.g. an unknown collection accessor). A `catch`
* block only ever needs to check for this one class:
*
* ```ts
* import { RebaseApiError } from "@rebasepro/client"; // re-exported
*
* try {
*   await client.data.products.update(id, { price: 9 });
* } catch (e) {
*   if (e instanceof RebaseApiError) {
*     if (e.status === 404) { ... }   // HTTP failures carry a status
*     console.error(e.code, e.details);
*   }
* }
* ```
*
* `status` is present for HTTP failures and `undefined` otherwise, so its
* presence distinguishes transport-level errors from realtime/logic errors.
*
* @group Errors
*/
var RebaseApiError = class extends Error {
	/** HTTP status code, or `undefined` for non-HTTP errors. */
	status;
	/** Stable machine-readable error code, when the server supplied one. See {@link RebaseErrorCode}. */
	code;
	/** Structured error payload from the server, when present. */
	details;
	/** See {@link RebaseErrorInit.requestId}. Quote it in a bug report. */
	requestId;
	/** See {@link RebaseErrorInit.retryAfterSeconds}. */
	retryAfterSeconds;
	constructor(message, init = {}) {
		super(message);
		this.name = "RebaseApiError";
		this.status = init.status;
		this.code = init.code;
		this.details = init.details;
		this.requestId = init.requestId;
		this.retryAfterSeconds = init.retryAfterSeconds;
		if (init.cause !== void 0) this.cause = init.cause;
	}
};
/**
* Client-side logic error — raised before any request is made (e.g. accessing
* an unknown collection accessor when a typed dictionary is configured).
*
* A subclass of {@link RebaseApiError} (with no `status`), so a single
* `catch (e) { if (e instanceof RebaseApiError) ... }` handles it too.
*
* @group Errors
*/
var RebaseClientError = class extends RebaseApiError {
	/**
	* `init` is the same one {@link RebaseApiError} takes, and it is what makes
	* `code` reachable at all.
	*
	* The constructor used to accept a message and nothing else, so every
	* client-side failure — an undefined filter value, an unknown accessor,
	* `listen()` on a client built with `realtime: false`, a function name with
	* a `/` in it, `refreshSession()` while signed out — arrived with `code ===
	* undefined`. The documented `switch (e.code)` in this file's own example
	* fell to `default: throw e` for all of them, and the only client-side error
	* that *did* carry a code was `OFFLINE`, because that one path minted a
	* `RebaseApiError` instead.
	*/
	constructor(message, init = {}) {
		super(message, init);
		this.name = "RebaseClientError";
	}
};
/**
* Brand for a contract method a particular client cannot serve.
*
* `Symbol.for` rather than a fresh symbol: two copies of `@rebasepro/types` in
* one tree — which happens, see `docs/dependency-duplication-traps.md` — must
* agree about it, and a module-local symbol would not.
*/
var UNSUPPORTED_METHOD = Symbol.for("rebase.unsupportedMethod");
function unsupportedMethod(message) {
	const stub = () => {
		throw new RebaseClientError(message, { code: "REALTIME_DISABLED" });
	};
	stub[UNSUPPORTED_METHOD] = true;
	return stub;
}
/**
* Can this method actually do anything?
*
* `true` for a stub from {@link unsupportedMethod} **and** for a method that is
* simply not there — a partial client, a hand-built test double, an
* implementation written against an older shape of the interface. Both mean the
* same thing to a caller, so both answer the same way, and an adapter that
* checks this cannot be caught out by either.
*
* Ordinary code does not need it: calling the method and letting it throw is
* the normal path. Adapters do — the admin panel chooses between subscribing
* and a one-shot `find()` by asking whether the client can listen, and a UI
* that subscribes into a throw is worse than one that polls. This is the
* question `if (accessor.listen)` used to be asking, made explicit now that the
* method is always there to call.
*
* @group Errors
*/
function isUnsupported(method) {
	if (typeof method !== "function") return true;
	return method[UNSUPPORTED_METHOD] === true;
}
//#endregion
//#region src/types/entities.ts
/**
* Class used to create a reference to a entity in a different path.
*
* @example
* // Simple reference (most common case - single driver, single db)
* new EntityReference({ id: "123", path: "users" })
*
* // Reference to a different driver (e.g., Firestore)
* new EntityReference({ id: "123", path: "analytics", driver: "firestore" })
*
* // Reference to a specific database within a driver
* new EntityReference({ id: "123", path: "orders", driver: "postgres", databaseId: "orders_db" })
*/
var EntityReference = class {
	__type = "reference";
	/**
	* ID of the entity
	*/
	id;
	/**
	* A string representing the path of the referenced document (relative
	* to the root of the database).
	*/
	path;
	/**
	* Which driver (e.g., 'postgres', 'firestore').
	* Defaults to "(default)" if not specified.
	*/
	driver;
	/**
	* Which database within the driver.
	* Defaults to "(default)" if not specified.
	*/
	databaseId;
	/**
	* Create a reference to a entity.
	*
	* @example
	* // Simple reference (most common case)
	* new EntityReference({ id: "123", path: "users" })
	*
	* // With driver
	* new EntityReference({ id: "123", path: "analytics", driver: "firestore" })
	*/
	constructor(props) {
		this.id = props.id;
		this.path = props.path;
		this.driver = props.driver;
		this.databaseId = props.databaseId;
	}
	get pathWithId() {
		return `${this.path}/${this.id}`;
	}
	/**
	* Get the full path including driver and database prefixes if specified.
	* For the common case (single driver, single db), this just returns pathWithId.
	*/
	get fullPath() {
		const parts = [];
		if (this.driver && this.driver !== "(default)") parts.push(this.driver);
		if (this.databaseId && this.databaseId !== "(default)") parts.push(this.databaseId);
		if (parts.length > 0) return `${parts.join(":")}:::${this.path}/${this.id}`;
		return this.pathWithId;
	}
	isEntityReference() {
		return true;
	}
};
/**
* Class used to create a reference to a entity in a different path
*/
var EntityRelation = class {
	__type = "relation";
	/**
	* ID of the entity
	*/
	id;
	/**
	* A string representing the path of the referenced document (relative
	* to the root of the database).
	*/
	path;
	/**
	* Pre-fetched data payload to eliminate N+1 queries.
	* When present, clients can use this directly instead of fetching.
	*/
	data;
	constructor(id, path, data) {
		this.id = id;
		this.path = path;
		this.data = data;
	}
	get pathWithId() {
		return `${this.path}/${this.id}`;
	}
	isEntityReference() {
		return false;
	}
	isEntityRelation() {
		return true;
	}
};
var GeoPoint = class {
	/**
	* The latitude of this GeoPoint instance.
	*/
	latitude;
	/**
	* The longitude of this GeoPoint instance.
	*/
	longitude;
	constructor(latitude, longitude) {
		this.latitude = latitude;
		this.longitude = longitude;
	}
};
var Vector = class {
	value;
	constructor(value) {
		this.value = value;
	}
};
//#endregion
//#region src/types/filter-operators.ts
/** The wire spelling of a {@link RelationAggregateSort}: `min(applications.created_at)`. */
var RELATION_AGGREGATE_SORT_PATTERN = /^(min|max|count|sum|avg)\(([^().]+)(?:\.([^()]+))?\)$/;
/**
* A {@link RelationAggregateSort} as a single string — `min(applications.created_at)`,
* `count(applications)`.
*
* The wire form is a string because every layer below the call site already is
* one: `OrderByTuple` is `[string, direction]`, the REST parameter is
* `?orderBy=key:direction`, the driver contract takes `orderBy?: string |
* OrderByTuple[]`, and a cursor names its keys by string. `_score` established
* the same pattern — a sort key that is not a column, spelled as one — and this
* reuses it rather than widening five signatures to carry an object that would
* be flattened at the end anyway.
*
* SQL's own spelling, so the key reads as what it compiles to. Neither `:` nor
* `,` appears in it, which is what keeps it safe in the colon-delimited wire
* shorthand.
*
* @group Models
*/
function encodeRelationAggregateSort(sort) {
	return `${sort.agg}(${sort.relation}${sort.field ? `.${sort.field}` : ""})`;
}
/**
* Read the string spelling back, or `undefined` if it is not one.
*
* `undefined` rather than a throw: this is asked of *every* sort key to find
* out which kind it is, and an ordinary column name is not an error.
*
* @group Models
*/
function parseRelationAggregateSort(key) {
	const match = RELATION_AGGREGATE_SORT_PATTERN.exec(key);
	if (!match) return void 0;
	const [, agg, relation, field] = match;
	if (!field && agg !== "count") return void 0;
	return {
		agg,
		relation,
		...field && { field }
	};
}
/** Is this sort key the object form rather than a field name? */
function isRelationAggregateSort(key) {
	return typeof key === "object" && key !== null && typeof key.relation === "string" && typeof key.agg === "string";
}
/** A sort key in the single-string form every layer below the call site speaks. */
function sortKeyToString(key) {
	return isRelationAggregateSort(key) ? encodeRelationAggregateSort(key) : key;
}
/** Maps canonical operators to their REST short-code equivalents. */
var CANONICAL_TO_REST = {
	"==": "eq",
	"!=": "neq",
	">": "gt",
	">=": "gte",
	"<": "lt",
	"<=": "lte",
	"in": "in",
	"not-in": "nin",
	"array-contains": "cs",
	"array-contains-any": "csa",
	"like": "like",
	"ilike": "ilike",
	"not-like": "nlike",
	"not-ilike": "nilike",
	"is-null": "isnull",
	"is-not-null": "notnull"
};
/** Maps REST short-code operators to their canonical equivalents. */
var REST_TO_CANONICAL = {
	"eq": "==",
	"neq": "!=",
	"gt": ">",
	"gte": ">=",
	"lt": "<",
	"lte": "<=",
	"in": "in",
	"nin": "not-in",
	"cs": "array-contains",
	"csa": "array-contains-any",
	"like": "like",
	"ilike": "ilike",
	"nlike": "not-like",
	"nilike": "not-ilike",
	"isnull": "is-null",
	"notnull": "is-not-null"
};
/**
* Operators that test for null/not-null and therefore ignore their value.
* Codecs normalize the value of these conditions to `null`.
*/
var NULL_OPS = /* @__PURE__ */ new Set(["is-null", "is-not-null"]);
/**
* Operators whose operand is a **list** of values rather than one value.
*
* On the wire that list is always parenthesised — `in.(draft,review)` — which
* is what lets the REST codec tell `?status=in.(a,b)` (the operator) from
* `?status=in.progress` (a value that happens to start with an operator's
* name). See `deserializeSingle` in `@rebasepro/common`.
*
* @group Models
*/
var LIST_OPS = /* @__PURE__ */ new Set([
	"in",
	"not-in",
	"array-contains-any"
]);
/**
* Every canonical operator, in a stable order. Useful for engine capability
* declarations ({@link DataSourceCapabilities.filterOperators}) and for
* building operator subsets.
* @group Models
*/
var ALL_WHERE_FILTER_OPS = [
	"<",
	"<=",
	"==",
	"!=",
	">=",
	">",
	"in",
	"not-in",
	"array-contains",
	"array-contains-any",
	"like",
	"ilike",
	"not-like",
	"not-ilike",
	"is-null",
	"is-not-null"
];
/** All canonical operator strings for runtime validation. */
var CANONICAL_OPS = new Set(ALL_WHERE_FILTER_OPS);
/**
* The REST table as a `Map`, because the key `toCanonicalOp` is handed comes
* off the wire.
*
* Indexed as a plain object, every `Object.prototype` member answered:
* `toCanonicalOp("valueOf")` returned the inherited *function* as though it
* were a `WhereFilterOp`, and every caller here treats a defined result as
* "known operator". Same defect the REST codec's own lookup tables were
* converted away from in `filter-dialect.ts`; this is the copy that survived
* one package over, and it now sits under the operator validation the REST
* parser does, which would otherwise have admitted `["constructor", x]`.
*/
var REST_OP_LOOKUP = new Map(Object.entries(REST_TO_CANONICAL));
/**
* Resolve any operator string (canonical or REST short-code) to its
* canonical `WhereFilterOp` form. Returns `undefined` for unknown operators.
*
* @example
* toCanonicalOp("==")   // "=="
* toCanonicalOp("eq")   // "=="
* toCanonicalOp("cs")   // "array-contains"
* toCanonicalOp("xyz")  // undefined
*/
function toCanonicalOp(op) {
	if (CANONICAL_OPS.has(op)) return op;
	return REST_OP_LOOKUP.get(op);
}
//#endregion
//#region src/types/admin_block.ts
/**
* The keys of a collection's admin block, as data.
*
* There is no *type* for the block in this package any more, and that is the point:
* `admin` is not declared on `BaseCollectionConfig` or on any property here, so a
* BaaS install cannot even write one. `@rebasepro/cms-types` adds the field back by
* declaration merging, which is why installing it is what makes the admin surface
* appear.
*
* The *list* still has to live here, because three runtime consumers need it and two
* of them are core — see below.
*/
/**
* Every key that belongs inside a collection's `admin` block, as data.
*
* The type that describes these fields is `AdminCollectionOptions` in
* `@rebasepro/cms-types`, and it is erased at build time — but three runtime
* consumers need the list, and two of them are core:
*
* - `serializeCollections`, to drop the block from the contract
* - the ts-morph schema editor in `@rebasepro/server`, which rewrites collection
*   files on disk from the admin panel and has to know where each key goes. A key
*   missing from this list gets written to the *top level* of the file, where the
*   backend ignores it and the panel never finds it again.
* - the `collections-admin-block` codemod
*
* `@rebasepro/cms-types` re-exports this and asserts it names only real option
* keys; the count is pinned by a test there.
*
* @group Models
*/
var ADMIN_COLLECTION_KEYS = [
	"Actions",
	"additionalFields",
	"alwaysApplyDefaultValues",
	"browserCallbacks",
	"components",
	"customViews",
	"defaultEntityAction",
	"defaultFilter",
	"defaultSelectedView",
	"defaultSize",
	"defaultViewMode",
	"disableDefaultActions",
	"display",
	"enabledViews",
	"entityActions",
	"entityViews",
	"exportable",
	"filterPresets",
	"fixedFilter",
	"form",
	"formAutoSave",
	"formView",
	"group",
	"hideFromEntityViews",
	"hideFromNavigation",
	"hideIdFromCollection",
	"hideIdFromForm",
	"icon",
	"includeJsonView",
	"inlineEditing",
	"kanban",
	"listProperties",
	"localChangesBackup",
	"openEntityMode",
	"orderProperty",
	"pagination",
	"previewProperties",
	"propertiesOrder",
	"selectionController",
	"selectionEnabled",
	"sideDialogWidth",
	"sort"
];
/**
* Every key that belongs inside a *property's* `admin` block, as data.
*
* The union of `AdminPropertyOptions` and its per-type extensions
* (`AdminStringOptions`, `AdminArrayOptions`, …) in `@rebasepro/cms-types`.
* It lives here for the same reason {@link ADMIN_COLLECTION_KEYS} does: the
* runtime consumers are core packages that the BaaS guard forbids from
* importing `@rebasepro/cms-types`. Here it is the boot-time collection
* validator in `@rebasepro/server`, which has to tell "you left `readOnly` at
* the top of the property, where nothing reads it" apart from "you invented a
* key we have never heard of".
*
* `@rebasepro/cms-types` re-exports this and asserts it names only real
* option keys.
*
* @group Models
*/
var ADMIN_PROPERTY_KEYS = [
	"canAddElements",
	"clearable",
	"columnWidth",
	"customProps",
	"disabled",
	"expanded",
	"Field",
	"Filter",
	"filterOperators",
	"fixedFilter",
	"format",
	"hideFromCollection",
	"includeEntityLink",
	"includeId",
	"markdown",
	"minimalistView",
	"multiline",
	"Preview",
	"previewAsTag",
	"previewProperties",
	"readOnly",
	"renderInForm",
	"sortable",
	"span",
	"spreadChildren",
	"urlPreview",
	"widget"
];
/**
* Move flattened admin keys back down into the `admin` block.
*
* The admin panel works with a *flat* view model — the block merged onto the
* collection — so what comes back from a form has `icon` and `defaultViewMode`
* at the top level while `admin` still holds whatever the file was loaded with.
* This is the way back.
*
* **The top-level value wins.** It is the one the form just wrote; the block is
* the copy the collection was loaded with, and preferring it resolves every edit
* in favour of the value the user changed away from.
*
* This lives here, next to the key lists, because it had two implementations —
* `toAdminCollectionConfig` in `@rebasepro/cms-types` and `nestAdminKeys` in
* `@rebasepro/server`'s schema editor — that agreed on everything except that
* precedence, which is the only part that decides whether a save is visible.
*
* @group Models
*/
function nestAdminKeysOf(source, adminKeys) {
	const keys = new Set(adminKeys);
	const top = {};
	const block = { ...source.admin ?? {} };
	for (const [key, value] of Object.entries(source)) {
		if (key === "admin") continue;
		if (keys.has(key)) block[key] = value;
		else top[key] = value;
	}
	if (Object.keys(block).length > 0) top.admin = block;
	return top;
}
/**
* {@link nestAdminKeysOf} for a collection.
*
* @group Models
*/
function nestAdminCollectionKeys(collection) {
	return nestAdminKeysOf(collection, ADMIN_COLLECTION_KEYS);
}
/** A record of properties, keyed by name — a map's `properties`, or a `oneOf` block's. */
function nestEachProperty(properties) {
	return Object.fromEntries(Object.entries(properties).map(([key, child]) => [key, isNestable(child) ? nestAdminPropertyKeys(child) : child]));
}
/** Anything the walk can descend into: a plain object, not an array. */
function isNestable(value) {
	return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}
/**
* {@link nestAdminKeysOf} for a property, applied to its children too.
*
* A map property carries `properties`, an array property carries `of`, and an
* array of typed blocks carries `oneOf.properties` — a record of properties like
* a map's. All of them hold properties with `admin` blocks of their own. A flat
* `readOnly` left on a child is as dead — and as fatal at the next boot — as one
* left on the parent, so the walk goes all the way down.
*
* `oneOf` was the container this walk did not know about, and it is the one the
* block-based collection templates are built out of: every block inside them
* kept its flat `markdown`, and the collection they created would not boot.
*
* @group Models
*/
function nestAdminPropertyKeys(property) {
	const nested = nestAdminKeysOf(property, ADMIN_PROPERTY_KEYS);
	const children = nested.properties;
	if (isNestable(children)) nested.properties = nestEachProperty(children);
	const oneOf = nested.oneOf;
	if (isNestable(oneOf) && isNestable(oneOf.properties)) nested.oneOf = {
		...oneOf,
		properties: nestEachProperty(oneOf.properties)
	};
	const of = nested.of;
	if (Array.isArray(of)) nested.of = of.map((entry) => isNestable(entry) ? nestAdminPropertyKeys(entry) : entry);
	else if (isNestable(of)) nested.of = nestAdminPropertyKeys(of);
	return nested;
}
//#endregion
//#region src/types/data_source.ts
/**
* The default data-source key, used when a collection does not name a
* `dataSource`. Shared by the frontend router and the backend driver
* registry so both agree on "the default database".
* @group Models
*/
var DEFAULT_DATA_SOURCE_KEY = "(default)";
/**
* Relation kinds assumed filterable when a driver does not say.
*
* `belongsTo` alone: its filter is a comparison on a column of the row being
* filtered, the one shape that needs no query construction a driver might not
* have. Everything else is a correlated subquery over another table.
*
* @group Models
*/
var DEFAULT_FILTERABLE_RELATION_KINDS = ["belongsTo"];
/** @group Models */
var POSTGRES_CAPABILITIES = {
	key: "postgres",
	label: "PostgreSQL",
	supportsRelations: true,
	supportsSubcollections: false,
	supportsRLS: true,
	supportsReferences: false,
	supportsColumnTypes: true,
	supportsRealtime: true,
	supportsVectors: true,
	filterOperators: ALL_WHERE_FILTER_OPS,
	filterableRelationKinds: [
		"belongsTo",
		"manyToMany",
		"hasMany",
		"hasOne"
	],
	supportsRelationFieldFilters: true,
	relationAggregateSorts: true,
	supportsSQLAdmin: true,
	supportsDocumentAdmin: false,
	supportsSchemaAdmin: true
};
/** @group Models */
var FIREBASE_CAPABILITIES = {
	key: "firestore",
	label: "Firebase / Firestore",
	supportsRelations: false,
	supportsSubcollections: true,
	supportsRLS: false,
	supportsReferences: true,
	supportsColumnTypes: false,
	supportsRealtime: true,
	supportsVectors: false,
	filterOperators: ALL_WHERE_FILTER_OPS.filter((op) => op !== "like" && op !== "ilike" && op !== "not-like" && op !== "not-ilike"),
	filterableRelationKinds: [],
	supportsRelationFieldFilters: false,
	relationAggregateSorts: false,
	supportsSQLAdmin: false,
	supportsDocumentAdmin: false,
	supportsSchemaAdmin: false
};
/** @group Models */
var MONGODB_CAPABILITIES = {
	key: "mongodb",
	label: "MongoDB",
	supportsRelations: false,
	supportsSubcollections: true,
	supportsRLS: false,
	supportsReferences: true,
	supportsColumnTypes: false,
	supportsRealtime: false,
	supportsVectors: false,
	filterOperators: ALL_WHERE_FILTER_OPS,
	filterableRelationKinds: [],
	supportsRelationFieldFilters: false,
	relationAggregateSorts: false,
	supportsSQLAdmin: false,
	supportsDocumentAdmin: true,
	supportsSchemaAdmin: true
};
/**
* Fallback capabilities when the driver is unknown.
* Enables everything so nothing is hidden unexpectedly.
* @group Models
*/
var DEFAULT_CAPABILITIES = {
	key: "(default)",
	label: "Default",
	supportsRelations: true,
	supportsSubcollections: true,
	supportsRLS: true,
	supportsReferences: true,
	supportsColumnTypes: true,
	supportsRealtime: true,
	supportsVectors: true,
	filterOperators: ALL_WHERE_FILTER_OPS,
	filterableRelationKinds: DEFAULT_FILTERABLE_RELATION_KINDS,
	supportsRelationFieldFilters: false,
	relationAggregateSorts: false,
	supportsSQLAdmin: true,
	supportsDocumentAdmin: true,
	supportsSchemaAdmin: true
};
var CAPABILITIES_REGISTRY = {
	postgres: POSTGRES_CAPABILITIES,
	firestore: FIREBASE_CAPABILITIES,
	mongodb: MONGODB_CAPABILITIES,
	"(default)": DEFAULT_CAPABILITIES
};
/**
* Look up capabilities for a given engine key.
* If `engine` is undefined or not found, returns `DEFAULT_CAPABILITIES`.
* @group Models
*/
function getDataSourceCapabilities(engine) {
	if (!engine) return POSTGRES_CAPABILITIES;
	return CAPABILITIES_REGISTRY[engine] ?? DEFAULT_CAPABILITIES;
}
/**
* Register custom capabilities for a third-party driver.
* @group Models
*/
function registerDataSourceCapabilities(capabilities) {
	CAPABILITIES_REGISTRY[capabilities.key] = capabilities;
}
//#endregion
//#region src/types/collections.ts
/**
* Type guard for PostgreSQL collections.
* Returns true if the collection uses the Postgres engine (or the default engine).
*
* Generic over the *input* type, and narrows by intersection rather than
* replacement. Narrowing to a bare `PostgresCollectionConfig` discarded whatever
* the caller actually had — most visibly the admin panel's view model, whose
* flattened presentation fields vanished the moment a collection passed through
* one of these guards.
*
* @group Models
*/
function isPostgresCollectionConfig(collection) {
	return !collection.engine || collection.engine === "postgres";
}
/**
* Narrows to the SQL collection fields — `table`, `relations`,
* `disableDefaultPolicies` — by asking the engine's declared capabilities
* rather than by naming Postgres.
*
* The two halves of this already existed and were never joined. The engine
* split (`PostgresCollectionConfig` / `FirebaseCollectionConfig` /
* `MongoDBCollectionConfig`) said which fields belong to which engine at the
* type level; {@link DataSourceCapabilities} said the same thing at runtime,
* down to a `supportsRelations` flag. So call sites guarded on the capability
* and then read a field the base type had to declare for them — which is why
* those fields were on the base, and why a MongoDB collection could be written
* with a `table`.
*
* Prefer this over {@link isPostgresCollectionConfig} wherever the question is
* "does this collection live in a SQL table", so a custom SQL engine
* registered through `registerDataSourceCapabilities` is included.
*
* @group Models
*/
function isRelationalCollectionConfig(collection) {
	return getDataSourceCapabilities(collection.engine).supportsRelations;
}
/**
* Type guard for Firebase / Firestore collections.
* @group Models
*/
function isFirebaseCollectionConfig(collection) {
	return collection.engine === "firestore";
}
/**
* Type guard for MongoDB collections.
* @group Models
*/
function isMongoDBCollectionConfig(collection) {
	return collection.engine === "mongodb";
}
/**
* Returns the data path for a collection.
* For Firestore or MongoDB collections with a `path`, returns that value;
* otherwise falls back to `slug`.
*/
function getCollectionDataPath(collection) {
	if (isFirebaseCollectionConfig(collection) && collection.path) return collection.path;
	if (isMongoDBCollectionConfig(collection) && collection.path) return collection.path;
	return collection.slug;
}
/**
* Reads a collection's driver-declared subcollections thunk (the `subcollections`
* field) independent of engine identity, so engine-agnostic code doesn't have to
* type-guard against a specific driver. Returns `undefined` when the collection
* declares none.
*
* Pair with `getDataSourceCapabilities(engine).supportsSubcollections` to decide
* whether the engine honours subcollections at all before reading them.
* @group Models
*/
function getDeclaredSubcollections(collection) {
	return collection.subcollections;
}
//#endregion
//#region src/types/search.ts
/** The column name used when {@link SearchConfig.column} is not given. */
var DEFAULT_SEARCH_COLUMN = "search_vector";
/** The matching strategy used when {@link SearchConfig.mode} is not given. */
var DEFAULT_SEARCH_MODE = "fts";
/** The text search configuration used when {@link SearchConfig.language} is not given. */
var DEFAULT_SEARCH_LANGUAGE = "simple";
/** The weight a field carries when it does not name one. */
var DEFAULT_SEARCH_WEIGHT = "B";
/** The similarity floor used when {@link SearchConfig.fuzzyThreshold} is not given. */
var DEFAULT_FUZZY_THRESHOLD = .3;
/**
* The relevance sort key. Valid only on a collection that declares a
* {@link SearchConfig} *and* on a query that carries a search string; anywhere
* else it is an unknown field and the request is refused.
*/
var RELEVANCE_SORT_FIELD = "_score";
//#endregion
//#region src/types/relations.ts
/**
* The key a junction row's own columns are carried under, in both directions.
*
* A read that includes a `manyToMany` relation serves each related row with its
* link's columns nested here — `{ id: 5, name: "ts", _pivot: { role: "owner" } }`
* — and a membership write may name the same key on an element to state what
* the link should hold. One constant because the two have to be the same word:
* a wire name that differs between the read and the write it round-trips
* through is a shape no client can echo back.
*
* Leading underscore, like `_matches`: it reads as metadata about the row
* rather than as one of its columns. A payload property may not be named
* `_pivot` either — `checkJunctionPayload` refuses it — so the key means one
* thing wherever it appears.
*
* @group Models
*/
var JUNCTION_PIVOT_KEY = "_pivot";
/** @group Models */
function hasForeignKeyOnTarget(relation) {
	return relation.kind === "hasOne" || relation.kind === "hasMany";
}
/** @group Models */
function isManyToMany(relation) {
	return relation.kind === "manyToMany";
}
/** @group Models */
function isToMany(relation) {
	return relation.cardinality === "many";
}
//#endregion
//#region src/types/policy.ts
/**
* The id a request without a logged-in user reports as `rebase.uid()`.
*
* A user-context request always sets `app.uid`: blank would read back as
* `NULL`, and `NULL` is how the trusted server context is recognised, so an
* anonymous visitor would be promoted to server privileges. The driver
* therefore substitutes this sentinel at the single chokepoint where the GUC
* is set.
*
* The consequence for policy authors is that **`rebase.uid() IS NOT NULL` is a
* tautology on the user path** — it is true for anonymous visitors too. Use
* {@link policy.authenticated} to mean "signed in", and
* {@link policy.serverContext} to mean "the trusted server context". Do not
* hand-write the comparison: see {@link ANONYMOUS_USER_IDS} for why one
* literal is not enough.
*
* @group Models
*/
var ANONYMOUS_USER_ID = "anonymous";
/**
* Every uid that has ever meant "nobody is signed in" — newest first.
*
* There are two because there were two. The types, the policy compiler, the
* JavaScript evaluator and the linter were all built on
* {@link ANONYMOUS_USER_ID}, while the request path scoped unauthenticated
* callers as `'anon'` — so `policy.authenticated()`, which compiled to
* `rebase.uid() <> 'anonymous'`, was *true* for an anonymous visitor. The
* sanctioned way to write "signed in" granted to everyone, and the linter
* flagged the spelling that actually worked as a foreign convention.
*
* The request path now reports {@link ANONYMOUS_USER_ID}. `'anon'` stays here
* because policies outlive the server that generated them: a database still
* holding policies from before the fix, or a project whose server has not been
* upgraded yet, must not become a grant in either direction. Compile against
* this list, not against a single literal.
*
* No real user id is ever one of these, so a match is always "not signed in".
*
* @group Models
*/
var ANONYMOUS_USER_IDS = [ANONYMOUS_USER_ID, "anon"];
/**
* Whether a uid stands for "no one is signed in", in any spelling rebase has
* used. `null`/`undefined` is the trusted server context, not an anonymous
* caller, and is therefore **not** anonymous — see {@link ANONYMOUS_USER_ID}.
*
* @group Models
*/
function isAnonymousUid(uid) {
	return typeof uid === "string" && ANONYMOUS_USER_IDS.includes(uid);
}
/** @group Models */
var policy = {
	true: () => ({ kind: "true" }),
	false: () => ({ kind: "false" }),
	and: (...operands) => ({
		kind: "and",
		operands
	}),
	or: (...operands) => ({
		kind: "or",
		operands
	}),
	not: (operand) => ({
		kind: "not",
		operand
	}),
	compare: (left, op, right) => ({
		kind: "compare",
		op,
		left,
		right
	}),
	rolesOverlap: (roles) => ({
		kind: "rolesOverlap",
		roles
	}),
	rolesContain: (roles) => ({
		kind: "rolesContain",
		roles
	}),
	authenticated: () => ({ kind: "authenticated" }),
	registered: () => ({ kind: "registered" }),
	serverContext: () => ({ kind: "serverContext" }),
	existsIn: (args) => ({
		kind: "existsIn",
		collection: args.collection,
		where: args.where
	}),
	raw: (sql) => ({
		kind: "raw",
		sql
	}),
	field: (name) => ({
		kind: "field",
		name
	}),
	outerField: (name) => ({
		kind: "outerField",
		name
	}),
	literal: (value) => ({
		kind: "literal",
		value
	}),
	authUid: () => ({ kind: "authUid" }),
	authRoles: () => ({ kind: "authRoles" }),
	authClaim: (name) => ({
		kind: "authClaim",
		name
	})
};
//#endregion
//#region src/types/rls-functions.ts
/**
* The SQL helper functions RLS policies call, and the schema they live in.
*
* ## One schema, and it is ours
*
* Rebase creates exactly one schema in a project's database: `rebase`. These
* three functions live in it alongside the framework's own tables, and that is
* the whole contract — a reader can look at a database and know precisely which
* namespace belongs to the framework and that nothing else was touched.
*
* It used to be two. `uid()`, `jwt()` and `roles()` sat in a schema called
* `auth`, which is Supabase's name, chosen so that a developer who had written
* Supabase RLS would recognise `auth.uid()`. The familiarity was real but the
* name was not Rebase's to take, and taking it had a concrete cost: pointing
* Rebase at a database that already had a Supabase `auth` schema meant
* `CREATE OR REPLACE FUNCTION auth.uid() RETURNS text` against Supabase's
* `RETURNS uuid`, which Postgres rejects outright —
*
*     ERROR:  cannot change return type of existing function
*     HINT:   Use DROP FUNCTION auth.uid() first.
*
* — and the failure landed inside a catch-all that logged a warning and carried
* on, leaving a database with auth tables, no helper functions, and policies
* calling functions that did not exist. Under `rebase db migrate` the same
* statements aborted the migration instead.
*
* `rebase.uid()` collides with nobody. A Supabase database keeps its `auth`
* schema untouched and gains a `rebase` one, which is what a gradual migration
* needs.
*
* ## Why functions at all, rather than inlining `current_setting`
*
* Because the indirection has already been spent once. `uid()` resolves
* `app.uid` and falls back to the pre-rename `app.user_id`, so that during a
* rolling deploy — old and new pods serving one database — both eras resolve
* the principal. That was a single `CREATE OR REPLACE`. Inlined into policy
* bodies it would have been a rewrite of every policy on every table.
*
* ## Why the name is not configurable
*
* A policy body is stored SQL: Postgres parses `USING (…)` once and keeps it, so
* these strings are written into every policy in every database Rebase has
* provisioned. Everything that reads policies back — the SQL-to-policy parser
* behind the admin UI, the drift checker, `rls-check` — would have to know the
* configured value to recognise its own output. One frozen name is the feature.
*/
/** The schema Rebase owns. The only schema Rebase creates. */
var REBASE_SCHEMA = "rebase";
/**
* The principal of the current request, as text, or NULL in the server context.
*
* Never NULL for a user request — an anonymous one carries
* {@link ANONYMOUS_USER_ID} — which is what makes `IS NULL` a reliable test for
* the trusted server plane and `IS NOT NULL` a tautology.
*/
var RLS_UID_SQL = `${REBASE_SCHEMA}.uid()`;
/** The request's roles as a comma-separated string, for `string_to_array`. */
var RLS_ROLES_SQL = `${REBASE_SCHEMA}.roles()`;
/**
* Whether the caller is a GUEST — signed in through anonymous sign-in rather
* than with an account.
*
* A different question from {@link ANONYMOUS_USER_ID}, and the two are easy to
* confuse: that sentinel means "no session at all", while this means "a session
* with nobody behind it". Anonymous sign-in mints a real user row with a real
* uid, so before this reached the database the two kinds of caller were one
* principal inside every policy.
*/
var RLS_IS_ANONYMOUS_SQL = `${REBASE_SCHEMA}.is_anonymous()`;
/** The request's JWT claims as `jsonb`, or `{}`. */
var RLS_JWT_SQL = `${REBASE_SCHEMA}.jwt()`;
/**
* The pre-1.0 spellings, for recognising policies and hand-written SQL that
* predate the move.
*
* Kept because policies outlive the server that wrote them: a database migrated
* by an older release still holds `auth.uid()` in its policy bodies until the
* next push or boot recompiles them, and anything that reads policies back has
* to recognise both eras or report the framework's own output as foreign drift.
* Also used to give a project whose `securityRules` contain raw `auth.uid()` a
* message naming the replacement, instead of a parse failure.
*/
var LEGACY_RLS_SCHEMA = "auth";
var LEGACY_RLS_UID_SQL = `${LEGACY_RLS_SCHEMA}.uid()`;
var LEGACY_RLS_ROLES_SQL = `${LEGACY_RLS_SCHEMA}.roles()`;
var LEGACY_RLS_JWT_SQL = `${LEGACY_RLS_SCHEMA}.jwt()`;
/**
* Rewrites the pre-1.0 function calls in a fragment of policy SQL.
*
* Deliberately anchored on a word boundary and the schema qualifier, so a column
* called `auth_uid` or a table named `auth` is left alone.
*/
function rewriteLegacyRlsFunctions(sql) {
	return sql.replace(/\bauth\.(uid|jwt|roles)\s*\(\s*\)/gi, (_match, fn) => `${REBASE_SCHEMA}.${fn.toLowerCase()}()`);
}
/** Whether a fragment of SQL still calls the pre-1.0 functions. */
function usesLegacyRlsFunctions(sql) {
	return /\bauth\.(uid|jwt|roles)\s*\(\s*\)/i.test(sql);
}
//#endregion
//#region src/types/tenancy.ts
/** Narrow a {@link TenantSource} to its claim form. @group Models */
function isTenantClaimSource(source) {
	return typeof source.claim === "string";
}
/** Narrow a {@link TenantSource} to its membership form. @group Models */
function isTenantMembershipSource(source) {
	return typeof source.membership === "object" && source.membership !== null;
}
/**
* The roles tenancy does not apply to, when the collection names none.
*
* `admin`, mirroring the security baseline every collection already carries
* (`<table>_default_admin_read` / `_write`): the Studio, `dataAsAdmin` and a
* support operator all run with it, and a tenancy rule that locked them out
* would make the admin panel show an empty table on a collection full of rows.
*
* @group Models
*/
var DEFAULT_TENANT_BYPASS_ROLES = ["admin"];
//#endregion
//#region src/types/backend.ts
/**
* Type guard: can this admin plan a live schema change?
*
* Planning is engine-specific — it renders DDL, a Drizzle schema and the
* declarative SQL artifacts — so the implementation lives in the driver
* package. The server detects the capability structurally, exactly as it does
* for SQL, rather than importing an engine it is supposed to know nothing
* about.
*
* @group Admin
*/
function isSchemaEditingAdmin(admin) {
	return !!admin && typeof admin.planSchemaChange === "function";
}
/**
* Type guard: does this admin support SQL operations?
* @group Admin
*/
function isSQLAdmin(admin) {
	return !!admin && typeof admin.executeSql === "function";
}
/**
* Type guard: does this admin support document operations?
* @group Admin
*/
function isDocumentAdmin(admin) {
	return !!admin && (typeof admin.executeAggregate === "function" || typeof admin.fetchCollectionStats === "function");
}
/**
* Type guard: does this admin support schema management?
* @group Admin
*/
function isSchemaAdmin(admin) {
	return !!admin && (typeof admin.fetchUnmappedTables === "function" || typeof admin.fetchTableMetadata === "function");
}
/**
* Type guard: does this admin support database branching?
* @group Admin
*/
function isBranchAdmin(admin) {
	return !!admin && typeof admin.createBranch === "function";
}
//#endregion
//#region src/types/schema_editing.ts
var DEFAULT_COMMIT_PATHS = {
	schemaFile: "backend/src/schema.generated.ts",
	ddlFile: "drizzle/schema.sql",
	policiesFile: "drizzle/policies.sql",
	searchFile: "drizzle/search.sql",
	vectorFile: "drizzle/vector.sql",
	triggersFile: "drizzle/triggers.sql"
};
//#endregion
//#region src/types/channel_bus.ts
/**
* Whether `setting` is an already-constructed transport rather than a request
* for a built-in one.
*
* Structural rather than nominal so that an instance from a *different copy* of
* `@rebasepro/types` — an entirely normal outcome of a separately versioned
* transport package — is still recognised.
*/
function isChannelBusInstance(setting) {
	return typeof setting?.publish === "function";
}
//#endregion
//#region src/types/resources.ts
/** The key a resource takes when a project declares only one of its kind. */
var DEFAULT_RESOURCE_KEY = "(default)";
var BRAND = Symbol.for("@rebasepro/types.resource");
/** Whether a value is a resource handle rather than a plain string key. */
function isResourceHandle(value) {
	return typeof value === "object" && value !== null && BRAND in value;
}
/** The key a resource reference names, whether it is a handle or already a key. */
function resourceKeyOf(ref) {
	return isResourceHandle(ref) ? ref.key : ref;
}
/**
* Replace every resource handle inside a value with its key, deeply.
*
* Applied where authored config becomes data: `defineCollection`, the
* collection loaders, the derive step. Past that point a collection is plain
* data that serialises, compares with `===` and reaches the admin UI over the
* wire, so a handle must not survive into it. Plain objects and arrays are
* walked; anything else — a function, a Date, a class instance — is a leaf and
* is returned as it is, which is what keeps callbacks and validators intact.
*/
function resolveResourceRefs(value) {
	if (isResourceHandle(value)) return value.key;
	if (Array.isArray(value)) {
		let changed = false;
		const out = value.map((item) => {
			const next = resolveResourceRefs(item);
			if (next !== item) changed = true;
			return next;
		});
		return changed ? out : value;
	}
	if (value !== null && typeof value === "object") {
		const proto = Object.getPrototypeOf(value);
		if (proto === Object.prototype || proto === null) {
			let changed = false;
			const out = {};
			for (const [k, v] of Object.entries(value)) {
				const next = resolveResourceRefs(v);
				if (next !== v) changed = true;
				out[k] = next;
			}
			return changed ? out : value;
		}
	}
	return value;
}
var GLOBAL_KEY = Symbol.for("@rebasepro/types.resourceRegistry");
/**
* Where kinds live, versioned — and why the version is in the symbol.
*
* Sharing one kinds map across copies means the copy that registers SECOND is
* the one that runs the comparison. That copy is whatever the bundle happens to
* carry, which for a driver is a build of this package frozen at its release —
* so the rule enforced is the rule that shipped THEN, not the one written here.
*
* `revision` (390bb03cd, applied to `database` in 346df48e2) was supposed to
* settle a disagreement between two copies, and it settles it only when the
* copy doing the arithmetic knows what `revision` is. 0.17.0–0.17.3 do not:
* they deep-equal the spec and throw. The runtime registers at import and a
* driver is imported after it, so the old copy is always second, always the
* judge, and always throws — verified by the bundle corpus on 2026-09-07,
* which reported v0.17.3's message verbatim ("Two packages cannot define the
* same kind.", no revision clause) while the runtime it ran on was 0.18.
*
* So copies that understand `revision` keep their kinds here, under a symbol
* no released copy looks at, and the legacy map is left to whoever still wants
* it. An old copy then registers into a map nobody contests, finds no existing
* entry, and cannot throw — in any load order, which is what the previous fix
* only claimed. Bumping this suffix again is how a future change to the
* REGISTRATION PROTOCOL is made; a change to a kind's own definition is still
* `revision`, among peers that share this map.
*/
var KINDS_KEY = Symbol.for("@rebasepro/types.resourceKinds.v2");
function registry() {
	const g = globalThis;
	let shared = g[GLOBAL_KEY];
	if (!shared) {
		shared = {
			kinds: /* @__PURE__ */ new Map(),
			declarations: /* @__PURE__ */ new Map()
		};
		g[GLOBAL_KEY] = shared;
	}
	let kinds = g[KINDS_KEY];
	if (!kinds) {
		kinds = /* @__PURE__ */ new Map();
		g[KINDS_KEY] = kinds;
	}
	return {
		kinds,
		legacyKinds: shared.kinds,
		declarations: shared.declarations
	};
}
/**
* Every kind visible to this copy: the versioned map, plus anything only a
* legacy copy registered.
*
* The fallback is not for Rebase's own kinds — this copy defines all of those —
* but for a third-party driver built against an older `@rebasepro/types` that
* registers a kind of its own. Dropping it would make that kind invisible and
* turn `declareResource` into "unknown resource kind" for something genuinely
* registered.
*/
function visibleKinds() {
	const { kinds, legacyKinds } = registry();
	if (legacyKinds.size === 0) return kinds;
	const merged = new Map(legacyKinds);
	for (const [k, v] of kinds) merged.set(k, v);
	return merged;
}
var amendments = /* @__PURE__ */ new Map();
/**
* Correct a registered kind without touching its registered literal.
*
* Use this, never an edit to the literal, for a kind that has shipped in a
* published package. The amendment applies to reads through this copy only.
*/
function amendResourceKind(kind, amendment) {
	amendments.set(kind, {
		...amendments.get(kind),
		...amendment
	});
}
/** A registered kind as this copy sees it: the shared literal plus this copy's amendments. */
function effectiveKind(spec) {
	const amendment = amendments.get(spec.kind);
	return amendment ? {
		...spec,
		...amendment
	} : spec;
}
/** `kind:key`, the graph's primary key. */
function declarationId(kind, key) {
	return `${kind}:${key}`;
}
/**
* Register a resource kind.
*
* Idempotent for an identical spec. When a spec for the same kind is already
* registered and differs, the `revision` decides: the higher one is kept and
* the other copy is warned about, in either load order. Two different specs at
* the SAME revision are a genuine conflict — two packages defining one kind, or
* a change that forgot to bump — and still throw.
*
* Both copies in that comparison are peers on `KINDS_KEY`, which is what makes
* the rule enforceable: a copy old enough not to know `revision` writes to the
* legacy map instead and never reaches this function's arithmetic. Registering
* a kind an older copy already put in the legacy map is therefore not a
* conflict — it is the ordinary case, and `visibleKinds` prefers this one.
*/
function registerResourceKind(spec) {
	const kinds = registry().kinds;
	const existing = kinds.get(spec.kind);
	if (!existing) {
		kinds.set(spec.kind, spec);
		return;
	}
	if (JSON.stringify(existing) === JSON.stringify(spec)) return;
	const have = existing.revision ?? 0;
	const incoming = spec.revision ?? 0;
	if (have === incoming) throw new Error(`Resource kind "${spec.kind}" is already registered with a different definition at revision ${have}. Two packages cannot define the same kind; a newer definition of the same kind must carry a higher \`revision\`.`);
	const [kept, dropped] = incoming > have ? [spec, existing] : [existing, spec];
	if (kept === spec) kinds.set(spec.kind, spec);
	console.warn(`[resources] Resource kind "${spec.kind}" is registered twice, at revisions ${dropped.revision ?? 0} and ${kept.revision ?? 0}; keeping revision ${kept.revision ?? 0}. The older copy is usually @rebasepro/types inlined in a driver built before the kind changed — rebuild the project with a current driver to remove it.`);
}
/** Every registered kind, for validators and for `rebase doctor`. */
function resourceKinds() {
	return [...visibleKinds().values()].map(effectiveKind);
}
/** One registered kind, or undefined. */
function resourceKind(kind) {
	const spec = visibleKinds().get(kind);
	return spec && effectiveKind(spec);
}
var COMMON_OPTION_KEYS = [
	"engine",
	"transport",
	"label"
];
/** Whether an engine is one the kind knows, or an explicit `custom:` opt-out. */
function isValidEngine(spec, engine) {
	return engine.startsWith("custom:") || spec.engines.includes(engine);
}
/**
* Declare a resource. The primitive every kind's constructor is built from.
*
* Redeclaring the same `kind:key` with a *different* shape throws rather than
* merging. Merging is what the old storage path did, and it silently discarded
* one of the two engines — a declaration accepted and then ignored, which is
* the failure this whole model exists to remove. Redeclaring it identically is
* fine: a config module evaluated twice must not be an error.
*/
function declareResource(kind, key = DEFAULT_RESOURCE_KEY, options = {}) {
	const spec = resourceKind(kind);
	if (!spec) {
		const known = [...visibleKinds().keys()].sort().join(", ") || "none";
		throw new Error(`Unknown resource kind "${kind}". Registered kinds: ${known}. Call registerResourceKind() before declaring one.`);
	}
	if (!key || typeof key !== "string" || key.trim() === "") throw new Error(`A ${kind} needs a non-empty key.`);
	const engine = options.engine ?? spec.defaultEngine;
	if (!isValidEngine(spec, engine)) throw new Error(`Unknown ${kind} engine "${engine}" for "${key}". Known engines: ${spec.engines.join(", ")}. An engine this build does not ship is spelled "custom:${engine}", which says so at the call site rather than failing later.`);
	const allowed = /* @__PURE__ */ new Set([...COMMON_OPTION_KEYS, ...spec.optionKeys ?? []]);
	const unknown = Object.keys(options).filter((k) => !allowed.has(k));
	if (unknown.length > 0) throw new Error(`Unknown option(s) on ${kind} "${key}": ${unknown.join(", ")}. A ${kind} accepts: ${[...allowed].sort().join(", ")}.`);
	const extra = {};
	for (const k of spec.optionKeys ?? []) if (options[k] !== void 0) extra[k] = options[k];
	const declaration = {
		kind,
		key,
		engine,
		transport: options.transport ?? "server",
		...options.label !== void 0 ? { label: options.label } : {},
		options: Object.freeze(extra)
	};
	const id = declarationId(kind, key);
	const previous = registry().declarations.get(id);
	if (previous) {
		if (JSON.stringify(previous) !== JSON.stringify(declaration)) throw new Error(`${kind} "${key}" is declared twice with different configuration. Declare it once and export it — two declarations of one resource is the ambiguity this model exists to remove, so it is refused rather than merged.`);
	} else registry().declarations.set(id, declaration);
	return {
		...declaration,
		toString() {
			return key;
		},
		[BRAND]: true
	};
}
/** Every declared resource, in declaration order, optionally filtered by kind. */
function declaredResources(kind) {
	const all = [...registry().declarations.values()];
	return kind ? all.filter((r) => r.kind === kind) : all;
}
/**
* Forget every declaration, keeping registered kinds.
*
* For tests and for a CLI that evaluates more than one project in a process.
* Kinds survive because they are registered by module import, which will not
* happen a second time.
*/
function resetDeclaredResources() {
	registry().declarations.clear();
}
/**
* The env-var suffix a resource's bindings use: `__ANALYTICS` for `analytics`,
* and nothing at all for the default-keyed one.
*
* The default takes no suffix so that a project with one database configured
* through plain `DATABASE_URL` keeps working having declared nothing — the
* overwhelmingly common project must not have to say so.
*/
function resourceEnvSuffix(key) {
	if (key === "(default)") return "";
	return `__${key.toUpperCase().replace(/[^A-Z0-9]+/g, "_").replace(/^_+|_+$/g, "")}`;
}
/**
* Two resources of a kind whose keys differ but whose env suffixes do not.
*
* `media-files` and `media_files` both become `__MEDIA_FILES`, so one would
* silently read the other's configuration. Returned rather than thrown so the
* caller can report it with the rest of a validation pass.
*/
function findEnvSuffixCollision(keys) {
	const seen = /* @__PURE__ */ new Map();
	for (const key of keys) {
		const suffix = resourceEnvSuffix(key);
		const previous = seen.get(suffix);
		if (previous !== void 0 && previous !== key) return {
			a: previous,
			b: key,
			suffix
		};
		seen.set(suffix, key);
	}
	return null;
}
/** The current graph format version. */
var RESOURCE_GRAPH_VERSION = 1;
/**
* Build a graph from the current declarations, sorted for a stable diff.
*
* `usedBy` maps a `kind:key` id to the things that reach it. The derive step
* supplies it after evaluating collections; a runtime building the graph at
* boot passes nothing and gets declarations alone, which is all it binds from.
*/
function buildResourceGraph(usedBy) {
	return {
		version: 1,
		resources: declaredResources().slice().sort((a, b) => a.kind.localeCompare(b.kind) || a.key.localeCompare(b.key)).map((r) => {
			const users = usedBy?.get(declarationId(r.kind, r.key));
			return users && users.length > 0 ? {
				...r,
				usedBy: [...users].sort()
			} : r;
		})
	};
}
/** `kind:key`, the id `usedBy` maps are keyed by. Exported for the derive step. */
function resourceId(kind, key) {
	return declarationId(kind, key);
}
/**
* The environment variables worth writing for a resource, given its engine.
*
* Falls back to every base the kind reads when the engine is unknown — a
* `custom:` engine gets the full list rather than an empty one, because
* guessing narrow would silently omit the variable it actually needs.
*/
function envBasesForResource(declaration) {
	const spec = resourceKind(declaration.kind);
	if (!spec) return [];
	return spec.envBasesByEngine?.[declaration.engine] ?? spec.envBases;
}
//#endregion
//#region src/types/storage_source.ts
/**
* Describes a named storage backend — a place files live.
*
* Declared once and shared front + back: the frontend uses it to decide
* transport (HTTP proxy vs direct SDK), the backend uses the same `key`
* to resolve a StorageController, and collection properties reference
* a definition by its `key` via `StorageConfig.storageSource`.
*
* This mirrors the {@link DataSourceDefinition} pattern used for databases.
*
* @group Models
*/
/**
* The default storage source key, used when a property does not specify
* a `storageSource`. Shared by the frontend and backend registries so
* both agree on "the default storage backend".
* @group Models
*/
var DEFAULT_STORAGE_SOURCE_KEY = "(default)";
/**
* The environment-variable suffix for a storage or data source key.
*
* `""` for the default source — so a single-bucket project keeps configuring
* plain `S3_BUCKET` — and `__<KEY>` for every named one, uppercased with
* non-alphanumerics collapsed to underscores: `media-cdn` → `S3_BUCKET__MEDIA_CDN`.
*
* The rule derives the variable name from the declared key rather than
* discovering keys by scanning the environment. Scanning would have to guess how
* `S3_BUCKET__MEDIA_CDN` splits into a key; deriving cannot be ambiguous, and a
* typo surfaces as a missing source at boot instead of a silently ignored
* variable.
*
* It lives in this package, with no dependencies, because four things must agree
* on it exactly: the CLI (validating a build), the runtime (reading its own
* environment), the control plane (writing a tenant's Secret), and the docs. A
* second implementation of a naming convention is a second chance to disagree.
*
* @group Models
*/
function storageEnvSuffix(key, defaultKey = DEFAULT_STORAGE_SOURCE_KEY) {
	if (!key || key === defaultKey) return "";
	const normalized = key.replace(/[^A-Za-z0-9]+/g, "_").replace(/^_+|_+$/g, "").toUpperCase();
	if (!normalized) throw new Error(`Source key "${key}" cannot be turned into an environment variable name. Use a key containing at least one letter or digit.`);
	return `__${normalized}`;
}
/**
* Two distinct keys that collapse onto the same variable name, or `null`.
*
* `media-cdn` and `media_cdn` are different source keys but the same suffix, so
* without this one of them silently reads the other's configuration. Returns the
* offending pair rather than throwing, so each caller can raise it in its own
* idiom — a `BundleError` at boot, a build failure in the CLI, a rejected deploy
* in a control plane.
*
* @group Models
*/
function findStorageSuffixCollision(keys, defaultKey = DEFAULT_STORAGE_SOURCE_KEY) {
	const seen = /* @__PURE__ */ new Map();
	for (const key of keys) {
		const suffix = storageEnvSuffix(key, defaultKey);
		const existing = seen.get(suffix);
		if (existing !== void 0 && existing !== key) return {
			a: existing,
			b: key,
			suffix
		};
		seen.set(suffix, key);
	}
	return null;
}
//#endregion
//#region src/types/resource_kinds.ts
/**
* The kinds Rebase ships, and the constructors a project declares them with.
*
* Each kind is registered rather than hardcoded, so a fourth one arrives
* without editing a manifest schema, a validator and a switch statement. That
* cost is precisely why databases and buckets ended up declared in different
* files with different rules — the cheapest thing to do was always to bolt the
* new kind onto whichever home was nearest.
*
* A kind owns its engine list. `custom:<id>` is always accepted, so a build
* that ships an engine this package has never heard of says so at the call site
* instead of looking like a typo of one that exists.
*/
registerResourceKind({
	revision: 1,
	kind: "database",
	engines: [
		"postgres",
		"mongodb",
		"firestore",
		"sqlite"
	],
	defaultEngine: "postgres",
	envBases: [
		"DATABASE_URL",
		"REBASE_DRIVER",
		"REBASE_DB_POOL_MAX"
	],
	optionKeys: [
		"databaseId",
		"migrations",
		"extensions"
	],
	implicitDefault: true
});
amendResourceKind("database", { envBases: [
	"DATABASE_URL",
	"DATABASE_READ_URL",
	"ADMIN_CONNECTION_STRING",
	"REBASE_DRIVER",
	"DB_POOL_MAX",
	"DB_POOL_IDLE_TIMEOUT",
	"DB_POOL_CONNECT_TIMEOUT"
] });
function database(keyOrOptions = DEFAULT_RESOURCE_KEY, options = {}) {
	return typeof keyOrOptions === "string" ? declareResource("database", keyOrOptions, options) : declareResource("database", DEFAULT_RESOURCE_KEY, keyOrOptions);
}
/**
* The extensions the project's databases gave Rebase leave to install.
*
* A flat union rather than a per-database answer, because the surfaces that ask
* — `rebase db push` and the boot schema-ensure — drive one connection and
* generate one `schema.sql` for every collection regardless of `dataSource`.
* Splitting the permission by data source would be a distinction the rest of
* that pipeline does not make, and a false precision is worse than none.
*
* Empty for a project that declared nothing, which is every project that has
* not opted in — so this reads as a refusal by default, on purpose.
*/
function declaredDatabaseExtensions() {
	const names = /* @__PURE__ */ new Set();
	for (const declaration of declaredResources("database")) {
		const declared = declaration.options.extensions;
		if (!Array.isArray(declared)) continue;
		for (const name of declared) if (typeof name === "string" && name.trim()) names.add(name.trim());
	}
	return [...names].sort();
}
registerResourceKind({
	kind: "bucket",
	engines: [
		"local",
		"s3",
		"gcs",
		"azure",
		"firebase"
	],
	defaultEngine: "local",
	envBases: [
		"S3_BUCKET",
		"GCS_BUCKET",
		"STORAGE_BUCKET",
		"STORAGE_PUBLIC_URL"
	],
	envBasesByEngine: {
		local: ["STORAGE_BUCKET"],
		s3: [
			"S3_BUCKET",
			"STORAGE_ENDPOINT",
			"STORAGE_REGION",
			"STORAGE_PUBLIC_URL"
		],
		gcs: ["GCS_BUCKET", "STORAGE_PUBLIC_URL"],
		azure: ["STORAGE_BUCKET", "STORAGE_PUBLIC_URL"],
		firebase: ["STORAGE_BUCKET", "STORAGE_PUBLIC_URL"]
	},
	optionKeys: [
		"publicRead",
		"prefix",
		"account"
	],
	implicitDefault: false
});
amendResourceKind("bucket", {
	optionKeys: [
		"publicRead",
		"prefix",
		"account",
		"default"
	],
	envBases: [
		"STORAGE_TYPE",
		"STORAGE_PATH",
		"S3_BUCKET",
		"S3_REGION",
		"S3_ACCESS_KEY_ID",
		"S3_SECRET_ACCESS_KEY",
		"S3_ENDPOINT",
		"S3_FORCE_PATH_STYLE",
		"GCS_BUCKET",
		"GCS_PROJECT_ID",
		"GCS_KEY_FILENAME"
	],
	envBasesByEngine: {
		local: ["STORAGE_TYPE", "STORAGE_PATH"],
		s3: [
			"STORAGE_TYPE",
			"S3_BUCKET",
			"S3_REGION",
			"S3_ACCESS_KEY_ID",
			"S3_SECRET_ACCESS_KEY",
			"S3_ENDPOINT",
			"S3_FORCE_PATH_STYLE"
		],
		gcs: [
			"STORAGE_TYPE",
			"GCS_BUCKET",
			"GCS_PROJECT_ID",
			"GCS_KEY_FILENAME"
		],
		azure: [],
		firebase: []
	}
});
function bucket(keyOrOptions = DEFAULT_RESOURCE_KEY, options = {}) {
	return typeof keyOrOptions === "string" ? declareResource("bucket", keyOrOptions, options) : declareResource("bucket", DEFAULT_RESOURCE_KEY, keyOrOptions);
}
registerResourceKind({
	kind: "topic",
	engines: ["jobs"],
	defaultEngine: "jobs",
	envBases: ["REBASE_TOPIC_URL"],
	optionKeys: ["delivery", "maxAttempts"],
	implicitDefault: false
});
amendResourceKind("topic", { envBases: [] });
var runtimeHolder = { current: null };
/** Install the transport topics publish through. Called by the server at boot. */
function setTopicRuntime(runtime) {
	runtimeHolder.current = runtime;
}
var subscriptions = [];
/** Every declared subscription, for the worker to wire and the graph to record. */
function declaredSubscriptions(topic) {
	return topic ? subscriptions.filter((s) => s.topic === topic) : subscriptions.slice();
}
/** Forget declared subscriptions. For tests, alongside `resetDeclaredResources`. */
function resetDeclaredSubscriptions() {
	subscriptions.length = 0;
}
/**
* Declare a topic.
*
* ```ts
* export const signups = topic<{ userId: string }>("signups");
* signups.subscription("send-welcome", async (event) => { … });
* await signups.publish({ userId });
* ```
*/
function topic(key, options = {}) {
	if (options.delivery === "at-most-once") throw new Error(`Topic "${key}" asks for at-most-once delivery, which no shipped transport implements. The durable queue behind topics retries, so it is at-least-once and a handler must tolerate seeing an event twice. Refused rather than quietly given the other guarantee.`);
	return {
		...declareResource("topic", key, options),
		toString() {
			return key;
		},
		async publish(event) {
			const runtime = runtimeHolder.current;
			if (!runtime) throw new Error(`Cannot publish to topic "${key}": no topic runtime is installed. Publishing works inside a running Rebase backend; this looks like config being evaluated outside one (a build, a script, or a test without a harness).`);
			await runtime.publish(key, event);
		},
		subscription(name, handler, subOptions = {}) {
			if (!name || name.trim() === "") throw new Error(`A subscription on topic "${key}" needs a non-empty name.`);
			if (subscriptions.some((s) => s.topic === key && s.name === name)) throw new Error(`Topic "${key}" already has a subscription named "${name}". The name is what a job row records and what a retry counts against, so two cannot share one.`);
			subscriptions.push({
				topic: key,
				name,
				handler,
				...subOptions.maxAttempts !== void 0 ? { maxAttempts: subOptions.maxAttempts } : {}
			});
		}
	};
}
registerResourceKind({
	kind: "cron",
	engines: ["scheduler"],
	defaultEngine: "scheduler",
	envBases: [],
	optionKeys: [
		"schedule",
		"timezone",
		"description",
		"enabled",
		"timeoutSeconds",
		"catchUpWindowSeconds"
	],
	implicitDefault: false
});
/**
* Declare a cron, as the scheduler's `defineCron` does on its way through.
*
* Projects do not call this: `defineCron` in `@rebasepro/server` does, so a
* cron file is both the handler and the declaration — one file, one name, and
* the graph derived from it says what a host needs to know without evaluating
* the handler. Exported so the derive step and the scheduler spell the
* declaration identically.
*/
function declareCron(name, options) {
	if (typeof options.schedule !== "string" || options.schedule.trim() === "") throw new Error(`Cron "${name}" needs a schedule — a five-field cron expression such as "0 3 * * *".`);
	return declareResource("cron", name, options);
}
registerResourceKind({
	kind: "function",
	engines: ["http"],
	defaultEngine: "http",
	envBases: [],
	optionKeys: [
		"portable",
		"requires",
		"file"
	],
	implicitDefault: false
});
/** Declare a function. Called by the derive step, not by projects. */
function declareFunction(name, options = {}) {
	return declareResource("function", name, options);
}
registerResourceKind({
	kind: "queue",
	engines: ["jobs"],
	defaultEngine: "jobs",
	envBases: [],
	optionKeys: ["maxAttempts"],
	implicitDefault: false
});
var queueRuntimeHolder = { current: null };
/** Install the transport queues enqueue through. Called by the server at boot. */
function setQueueRuntime(runtime) {
	queueRuntimeHolder.current = runtime;
}
var queueConsumers = /* @__PURE__ */ new Map();
/** Every declared queue handler, for the worker to wire. */
function declaredQueueConsumers() {
	return [...queueConsumers.values()];
}
/** Forget declared queue handlers. For tests, alongside `resetDeclaredResources`. */
function resetDeclaredQueueConsumers() {
	queueConsumers.clear();
}
/**
* Declare a queue.
*
* ```ts
* export const thumbnails = queue<{ key: string }>("thumbnails");
* thumbnails.handler(async ({ key }) => { … });
* await thumbnails.enqueue({ key }, { runAt: new Date(Date.now() + 60_000) });
* ```
*
* The difference from a topic is the number of consumers: a queue has one, a
* topic fans out to every subscription. Both ride on the durable job queue, so
* declaring either turns it on.
*/
function queue(key, options = {}) {
	return {
		...declareResource("queue", key, options),
		toString() {
			return key;
		},
		async enqueue(payload, enqueueOptions) {
			const runtime = queueRuntimeHolder.current;
			if (!runtime) throw new Error(`Cannot enqueue on queue "${key}": no queue runtime is installed. Enqueueing works inside a running Rebase backend; this looks like config being evaluated outside one (a build, a script, or a test without a harness).`);
			return runtime.enqueue(key, payload, enqueueOptions);
		},
		handler(fn) {
			if (queueConsumers.has(key)) throw new Error(`Queue "${key}" already has a handler. A queue has exactly one consumer; work that several things react to is a topic with several subscriptions.`);
			queueConsumers.set(key, {
				queue: key,
				handler: fn
			});
		}
	};
}
/**
* One declaration, as the data layer's definition.
*
* There is exactly one of these per kind, and everything that needs a
* definition goes through it — the frontend, the managed runtime's boot path,
* and an ejected project's own entrypoint. That is not tidiness: the mapping
* used to exist twice, once here and once in `@rebasepro/server`'s
* `graphToStorageSources`, and the two disagreed. The server's copy carried a
* bucket's `account`; this one dropped it, so a bucket declared with shared
* credentials resolved them on the managed runtime and resolved *nothing* in an
* ejected backend — the source was skipped and every upload to it answered 501.
*
* A field-by-field map is one line away from that failure at all times, so
* there is now one line to keep right instead of two to keep equal.
*/
function resourceToDataSource(declaration) {
	return {
		key: declaration.key === "(default)" ? DEFAULT_DATA_SOURCE_KEY : declaration.key,
		engine: declaration.engine,
		transport: declaration.transport,
		...typeof declaration.options.databaseId === "string" ? { databaseId: declaration.options.databaseId } : {},
		...declaration.label !== void 0 ? { label: declaration.label } : {}
	};
}
/** One declaration, as the storage layer's definition. See {@link resourceToDataSource}. */
function resourceToStorageSource(declaration) {
	return {
		key: declaration.key === "(default)" ? DEFAULT_STORAGE_SOURCE_KEY : declaration.key,
		engine: declaration.engine,
		transport: declaration.transport,
		...typeof declaration.options.account === "string" ? { account: declaration.options.account } : {},
		...declaration.options.default === true ? { default: true } : {},
		...declaration.label !== void 0 ? { label: declaration.label } : {}
	};
}
/**
* The declared databases, as definitions.
*
* Both the frontend and a project's own backend entrypoint read this. The
* frontend needs to know which sources exist and how they are reached — a
* `direct`-transport source is one the browser talks to itself — and it imports
* the same config package the backend does. Without these it would mean writing
* the list a second time, by hand, next to the declarations, which is precisely
* the two-homes problem this model removed everywhere else.
*
* ```tsx
* import "../config/resources";                 // registers them
* import { declaredDataSources, declaredStorageSources } from "@rebasepro/types";
*
* <Rebase dataSources={declaredDataSources()} storageSources={declaredStorageSources()} />
* ```
*
* The import is what registers them, so a bundler that drops an unused module
* would leave this empty — hence the side-effect import above rather than a
* bare re-export.
*/
function declaredDataSources() {
	return declaredResources("database").map(resourceToDataSource);
}
/** The declared buckets, as definitions. */
function declaredStorageSources() {
	return declaredResources("bucket").map(resourceToStorageSource);
}
//#endregion
//#region src/types/env_boolean.ts
var YES = /* @__PURE__ */ new Set([
	"true",
	"1",
	"yes",
	"on"
]);
var NO = /* @__PURE__ */ new Set([
	"false",
	"0",
	"no",
	"off"
]);
/**
* Read a boolean environment variable — one spelling rule for every reader.
*
* `true`, `1`, `yes` and `on` are true; `false`, `0`, `no` and `off` are
* false. Case and surrounding whitespace are ignored. Anything else — unset,
* blank, or a value that spells neither — is `undefined`, so the caller's
* default decides and a typo lands on the side the caller chose:
*
* ```ts
* parseEnvBoolean(env.FORCE_LOCAL_STORAGE) === true            // off unless said
* parseEnvBoolean(env.REBASE_MCP_OPEN_REGISTRATION) !== false  // on unless said
* ```
*
* It exists because every reader used to spell this itself, and they
* disagreed. The reader that mattered tested the raw string for truthiness —
* `!process.env.FORCE_LOCAL_STORAGE` — and every non-empty string is truthy,
* so `FORCE_LOCAL_STORAGE=false`, written to say "there is no durable volume
* here", switched the production storage guard off and sent uploads to a
* container filesystem the next redeploy erased. Beside it, `=== "true"` made
* `DISABLE_DB_ROLE_SWITCHING=1` do nothing and `!== "false"` made
* `REBASE_MCP_OPEN_REGISTRATION=0` leave registration open.
*
* The boot schemas in `@rebasepro/server` (`loadEnv`, `loadBootEnv`) accept a
* strict subset — `true`, `false` and blank — and refuse any other value
* before the server starts. On that subset they read exactly what this reads,
* so a variable a schema declares and something else reads lazily cannot mean
* two different things.
*
* It lives in this package, with no dependencies, for the reason
* {@link storageEnvSuffix} does: the runtime, its drivers and the CLI all read
* these variables, and a second parser is a second chance to disagree.
*
* @group Models
*/
function parseEnvBoolean(value) {
	if (value === void 0) return void 0;
	const spelled = value.trim().toLowerCase();
	if (YES.has(spelled)) return true;
	if (NO.has(spelled)) return false;
}
//#endregion
//#region src/types/component_ref.ts
/**
* Type guard: checks if a value is a `LazyComponentRef` produced by the
* Vite transform plugin.
*/
function isLazyComponentRef(ref) {
	return typeof ref === "object" && ref !== null && "__rebaseLazy" in ref && ref.__rebaseLazy === true;
}
//#endregion
//#region src/types/project_manifest.ts
/**
* Path prefixes the backend owns, which no static app may claim.
*
* One process — and, on the platform, one hostname — serves both the API and
* however many static apps a project has. Mounting is longest-path-first, so an
* app declaring `/api` would win against the API itself and every request to it
* would be answered with that app's `index.html`: a 200 carrying HTML where the
* caller expected JSON, from a project that looks deployed and healthy.
*
* Declared here rather than in either enforcer because both must agree. The CLI
* checks it so a developer finds out while editing `rebase.json`; the control
* plane checks it again at deploy intake, because the front door's correctness
* cannot rest on a check that ran in somebody else's CLI — and a repository can
* be deployed by a CLI older than this rule.
*/
var RESERVED_BACKEND_PREFIXES = [
	"/api",
	"/health",
	"/healthz",
	"/livez",
	"/readyz",
	"/metrics"
];
/**
* Whether `path` collides with a prefix the backend owns.
*
* Compares at segment boundaries, so `/api` and `/api/v2` collide while
* `/apidocs` does not — the same rule the router matches with, because a check
* that is stricter than the router rejects paths that would have worked, and one
* that is looser admits paths that will not.
*/
function reservedPrefixFor(path) {
	const normalized = path.endsWith("/") && path !== "/" ? path.slice(0, -1) : path;
	return RESERVED_BACKEND_PREFIXES.find((reserved) => normalized === reserved || normalized.startsWith(`${reserved}/`));
}
/**
* Version of the bundle *format* itself.
*
* Bumped only when the on-disk layout changes in a way an older runtime could
* not read. A runtime accepts any bundle whose `bundleFormat` is less than or
* equal to its own — old bundles keep booting on new runtimes, which is the
* whole point of separating the artifact from the engine.
*
* - **1** — `mode: "cms" | "baas" | "static"`, `entry.static` a single directory
*   string, `entry.admin` for a bundled admin panel.
* - **2** — `kind: "backend" | "static"`, `entry.static` a list of
*   {@link RebaseBundleStatic}, `entry.admin` removed. A format-1 runtime reading
*   one of these would find no `mode` and an array where it expects a string, so
*   the bump is what turns that into a refusal to boot instead of a bundle that
*   starts and serves nothing.
*/
var BUNDLE_FORMAT_VERSION = 2;
/**
* The runtime contract major.
*
* Distinct from the `@rebasepro/server` package version: the package may release
* any number of minors and patches while this stays put. It changes only when
* the bundle/runtime contract breaks compatibility, and a project's
* `manifest.runtime` range is matched against *this*.
*
* ## v2 — resources are declared, not configured
*
* `RebaseBackendConfig.dataSources` and `.storageSources` are gone. A project
* declares its databases and buckets with `database()` / `bucket()` in its
* config, and the runtime reads those declarations.
*
* This had to be a major, and the reason is the managed tier: it moves projects
* onto new images WITHOUT rebuilding them. A bundle built against v1 exports
* those keys, and a v2 runtime refuses them at boot — so without this bump, one
* image rollout would crash-loop every tenant that had ever declared a second
* database or bucket, in a wave, with the cause in a container log nobody is
* watching.
*
* With the bump, a v1 bundle on a v2 runtime is refused by
* `assertBundleCompatibility` with the remedy in the message, and the platform
* keeps it on a v1 image until it is rebuilt. That is the whole purpose of this
* number.
*
* **Release order matters and is not optional.** The control plane is the side
* that rejects, so it ships FIRST: raise `SUPPORTED_RUNTIME_CONTRACT` in the
* saas repo (it rejects only `contract >` its own, so it then accepts both),
* deploy that, and only then release a runtime implementing v2. Shipping the
* runtime first turns every deploy into a rejected intake blaming the tenant's
* bundle.
*/
var RUNTIME_CONTRACT_VERSION = 1;
/** Header carrying the schema version an SDK was generated from. */
var SCHEMA_VERSION_HEADER = "x-rebase-schema";
//#endregion
//#region src/types/collection_contract.ts
function isSerializedCollectionRef(value) {
	return typeof value === "object" && value !== null && typeof value.__collectionRef === "string";
}
/** Depth limit for the walk — deep enough for real configs, finite for cyclic ones. */
var MAX_DEPTH = 64;
/**
* Resolve whatever a `target` thunk returns down to a collection.
*
* A target may be the collection, a module namespace (when the authoring file
* used `import * as`), or a default-export wrapper. All three appear in real
* projects, and the SDK generator already unwraps them the same way.
*/
function unwrapTarget(value) {
	if (!value || typeof value !== "object") return void 0;
	const candidate = value;
	if (candidate.default || candidate.__esModule) {
		const inner = candidate.default;
		if (inner && typeof inner === "object") return inner;
	}
	if (candidate.properties) return value;
}
/** The identity a serialized reference uses. Slug first — it is the routing key. */
function refFor(collection) {
	if (!collection) return void 0;
	const withPath = collection;
	return collection.slug || withPath.path || collection.name;
}
function toSerializable(value, seen, depth, state, key) {
	if (depth > MAX_DEPTH) {
		state.truncations++;
		return;
	}
	if (typeof value === "function") {
		if (key === "target") try {
			const ref = refFor(unwrapTarget(value()));
			return ref ? { __collectionRef: ref } : void 0;
		} catch {
			return;
		}
		return;
	}
	if (value === null || typeof value !== "object") return value;
	if (value instanceof Date) return value.toISOString();
	if (value instanceof RegExp) return value.source;
	if (seen.has(value)) {
		state.truncations++;
		return;
	}
	const cached = state.memo.get(value);
	if (cached !== void 0) return cached;
	seen.add(value);
	const truncationsBefore = state.truncations;
	const memoize = (result) => {
		if (result !== void 0 && state.truncations === truncationsBefore) state.memo.set(value, result);
		return result;
	};
	try {
		if (Array.isArray(value)) {
			const items = value.map((item) => toSerializable(item, seen, depth + 1, state)).filter((item) => item !== void 0);
			return memoize(value.length > 0 && items.length === 0 ? void 0 : items);
		}
		if ("$$typeof" in value) return void 0;
		const entries = Object.entries(value);
		const out = {};
		for (const [k, v] of entries) {
			const converted = toSerializable(v, seen, depth + 1, state, k);
			if (converted !== void 0) out[k] = converted;
		}
		if (entries.length > 0 && Object.keys(out).length === 0) return void 0;
		return memoize(out);
	} finally {
		seen.delete(value);
	}
}
/**
* Serialize collections for transport over the contract endpoint.
*
* Sorted by slug so the output — and therefore the schema hash computed from it
* — does not depend on filesystem ordering.
*/
function serializeCollections(collections) {
	return [...collections].sort((a, b) => String(a.slug ?? "").localeCompare(String(b.slug ?? ""))).map((collection) => toSerializable(withoutAdminBlock(collection), /* @__PURE__ */ new WeakSet(), 0, {
		memo: /* @__PURE__ */ new WeakMap(),
		truncations: 0
	})).filter((c) => c !== void 0);
}
/**
* Drop the admin block before the walk.
*
* Nothing downstream of serialization is an admin panel. The contract endpoint
* feeds remote SDK generation, and `rebase build` writes the result into a bundle
* manifest that only the backend runtime reads. The block would survive the walk
* as a husk anyway — its React elements and component functions are dropped
* individually — and that husk has two costs worth avoiding: it puts every custom
* component's *file path* on an endpoint whose job is to describe data shapes, and
* it grows a payload that is fetched and cached per project.
*
* Removing it here rather than at each call site means one chokepoint, so a future
* consumer of `serializeCollections` cannot forget.
*
* Child collections carry their own block, so this recurses — stripping only the
* top level was the mistake `stripNonClientFields` in the contract routes already
* had to fix once for security rules.
*/
function withoutAdminBlock(collection) {
	const { admin: _admin, ...rest } = collection;
	const nested = rest;
	if (Array.isArray(nested.subcollections)) nested.subcollections = nested.subcollections.map((child) => withoutAdminBlock(child));
	return rest;
}
/**
* Rebuild collections received from a contract endpoint.
*
* Relation refs become real thunks resolving through the returned set, so
* downstream consumers — the SDK generator above all — see exactly the shape
* they would have seen had the collections been imported from source.
*
* A ref naming a collection that is not in the payload resolves to `undefined`
* rather than throwing: the generator already tolerates an unresolvable target
* by falling back to a permissive key type, and a partial contract should still
* produce a usable SDK.
*/
function deserializeCollections(payload) {
	const collections = payload.filter((c) => typeof c === "object" && c !== null).map((c) => ({ ...c }));
	const bySlug = /* @__PURE__ */ new Map();
	for (const collection of collections) {
		const ref = refFor(collection);
		if (ref) bySlug.set(ref, collection);
	}
	const rehydrate = (value, depth) => {
		if (depth > MAX_DEPTH || !value || typeof value !== "object") return;
		if (Array.isArray(value)) {
			for (const item of value) rehydrate(item, depth + 1);
			return;
		}
		const record = value;
		for (const [key, child] of Object.entries(record)) {
			if (key === "target" && isSerializedCollectionRef(child)) {
				const slug = child.__collectionRef;
				record.target = () => bySlug.get(slug);
				continue;
			}
			rehydrate(child, depth + 1);
		}
	};
	for (const collection of collections) rehydrate(collection, 0);
	return collections;
}
//#endregion
//#region src/types/schema_version.ts
/**
* The schema version stamp.
*
* One function, used in three places that must agree or the whole drift-detection
* story is noise: `rebase build` writes it into a bundle manifest, the runtime
* serves it from the contract endpoint, and a generated SDK records the value it
* was built from. If any two of those computed it differently, every client would
* look permanently out of date.
*
* It covers **collections only** — the client's contract is the shape of the
* data, so editing a hook or a server function must not invalidate every SDK in
* every repository. That is a deliberate narrowing, not an oversight.
*/
/** Stable stringify: object keys sorted at every level, so key order cannot alter the hash. */
function canonicalize(value) {
	if (value === null || typeof value !== "object") return JSON.stringify(value) ?? "null";
	if (Array.isArray(value)) return `[${value.map(canonicalize).join(",")}]`;
	return `{${Object.entries(value).filter(([, v]) => v !== void 0).sort(([a], [b]) => a < b ? -1 : a > b ? 1 : 0).map(([k, v]) => `${JSON.stringify(k)}:${canonicalize(v)}`).join(",")}}`;
}
/**
* Reduce a collection to the parts a generated client is actually built from.
*
* The version answers one question — "is this SDK stale?" — so it must change
* exactly when the generated types could change, and never otherwise. Hashing a
* whole collection fails both halves of that:
*
* - Security rules, callbacks, icons, groups and UI settings do not appear in a
*   generated client, so including them reports perfectly current SDKs as stale.
* - Worse, they are not stable *inputs*. The runtime applies default security
*   rules when it loads collections, so the same source hashed before and after
*   loading produced two different answers — a build-time stamp that could never
*   match the server that served it.
*
* Codegen reads the slug (for the `Database` key and type names), the properties,
* and the relations. That is the projection.
*/
function projectForCodegen(collection) {
	const source = collection;
	return {
		slug: collection.slug ?? source.path,
		properties: collection.properties,
		relations: source.relations,
		engine: source.engine,
		dataSource: source.dataSource,
		subcollections: source.subcollections?.map(projectForCodegen)
	};
}
/**
* Compute the canonical string a schema version hashes.
*
* Exposed separately so the hashing itself can differ by environment: Node has
* `crypto`, and callers without it can still compare canonical forms directly.
*/
function canonicalSchemaPayload(collections) {
	return canonicalize(serializeCollections(collections).map((collection) => projectForCodegen(collection)));
}
/**
* A short, non-cryptographic digest of the canonical payload.
*
* FNV-1a style, 64 bits, as two 32-bit halves. This is an identity, not a
* security boundary: nothing trusts a schema version to prove anything, it only
* answers "is this the same schema as before". A hand-rolled hash keeps this
* module free of `node:crypto`, so the identical function runs in the browser,
* in the CLI, and in the runtime — which is the property that actually matters.
*/
function computeSchemaVersion(collections) {
	const payload = canonicalSchemaPayload(collections);
	let h1 = 2166136261;
	let h2 = 16777619;
	for (let i = 0; i < payload.length; i++) {
		const code = payload.charCodeAt(i);
		h1 ^= code;
		h1 = h1 + ((h1 << 1) + (h1 << 4) + (h1 << 7) + (h1 << 8) + (h1 << 24)) >>> 0;
		h2 ^= code + i;
		h2 = h2 + ((h2 << 1) + (h2 << 5) + (h2 << 9) + (h2 << 15) + (h2 << 24)) >>> 0;
	}
	const hex = (n) => n.toString(16).padStart(8, "0");
	return `v1:${hex(h1)}${hex(h2)}`;
}
//#endregion
//#region src/controllers/data.ts
/**
* Hops an {@link IncludeSpec} may nest. `comments.author` is two.
*
* @group Data
*/
var MAX_INCLUDE_DEPTH = 3;
/**
* SDK collection client — returns flat rows, no Entity wrapper.
*
* This is the public API surface for app developers using
* `createRebaseClient()`. admin internals use `CollectionAccessor` instead.
*
* Type parameters:
* - `M` — the **Row** shape returned by reads (`find`, `findById`, `listen`).
* - `I` — the **Insert** shape accepted by {@link create}. Defaults to
*   `Partial<M>`; the generated SDK supplies a dedicated `Insert` type where
*   required columns are required and auto-generated / read-only columns are
*   omitted, so `create({})` on a table with required fields is a compile error.
* - `U` — the **Update** shape accepted by {@link update}. Defaults to
*   `Partial<M>`; the generated SDK supplies a dedicated `Update` type.
*
* @example
* const { data: posts } = await rebase.data.posts.find();
* console.log(posts[0].title);       // flat access
* console.log(posts[0].id);          // id at top level
*
* const post = await rebase.data.posts.findById(1);
* console.log(post?.title);          // no .values needed
*
* @group Data
*/
/**
* A change expressed as an operation on the column's current value, rather than
* as the value to store.
*
* `{ views: 5 }` says what the number becomes; `{ views: { $inc: 1 } }` says
* what happens to it. The difference is the read the caller no longer has to
* make — and the race that read opens. Two requests that each read `4`, add one
* and write `5` lose an increment between them; `SET views = views + 1` cannot,
* because the arithmetic happens inside the statement holding the row lock.
*
* Exactly one operator per field. `{ views: { $inc: 1, $push: "x" } }` is
* refused rather than applied in an order the caller cannot see.
*
* @group Data
*/
/**
* The operator names, as a value.
*
* A runtime list beside the type because three layers have to *recognise* an
* operation, not just accept one: the REST validator, the driver that compiles
* it, and the offline queue that must refuse to apply one locally. Three copies
* of four strings is three chances for one of them to miss an operator added to
* the other two, and the failure is silent in the worst direction — an
* unrecognised marker is written to the column as a JSON document.
*
* @group Data
*/
var FIELD_OPERATORS = [
	"$inc",
	"$push",
	"$pull",
	"$merge"
];
/**
* The key of a {@link BatchRef}. Declared here, beside the field operators,
* because the two share one namespace: a `$`-prefixed key in a write payload is
* a marker, and every reader of that namespace has to know all of it.
*
* @group Data
*/
var BATCH_REF_KEY = "$ref";
/**
* Whether a value is *trying* to be a field operation — including a misspelled
* one, which is the case worth catching.
*
* Any `$`-prefixed key counts, because `{ $increment: 1 }` written to a number
* column as a JSON document is the failure this exists to prevent. No collection
* can declare a column whose value legitimately has a key beginning with `$`: a
* `map` property's sub-keys are declared, and `$` is not valid in the
* identifiers the DDL generators emit.
*
* The one exception is `{ $ref: … }`, the batch's backward reference. It stands
* where a *value* goes and is resolved to one before the row is written, so it
* is not an operation on a column — reading it as a misspelled operator refused
* every `$ref` in a batch with "unknown field operator '$ref'".
*
* @group Data
*/
function isFieldOperation(value) {
	if (typeof value !== "object" || value === null || Array.isArray(value) || value instanceof Date) return false;
	const keys = Object.keys(value);
	if (keys.length === 1 && keys[0] === "$ref") return false;
	return keys.some((key) => key.startsWith("$"));
}
/** True when any value in a write payload is (or is attempting to be) one. @group Data */
function hasFieldOperation(values) {
	return !!values && Object.values(values).some(isFieldOperation);
}
//#endregion
//#region src/controllers/data_driver.ts
/** Rows returned for a plain / text-search list read when the client sends no `limit`. */
var DEFAULT_LIST_LIMIT = 50;
/** Rows returned for a vector-search list read when the client sends no `limit`. */
var DEFAULT_VECTOR_LIST_LIMIT = 10;
/** Largest `limit` a client may ask for on any surface. Above it, the read is refused. */
var MAX_LIST_LIMIT = 1e3;
/**
* Thrown by {@link resolveClientListLimit} for a `limit` the platform will not
* serve. Carries an HTTP status so an ingress that speaks HTTP can forward it
* verbatim, and `maxLimit` so one can be built without re-deriving the ceiling.
*
* @group Errors
*/
var ListLimitError = class ListLimitError extends RebaseApiError {
	/** The ceiling that was exceeded — what the caller should page by instead. */
	maxLimit;
	constructor(message, maxLimit) {
		super(message, {
			status: 400,
			code: "INVALID_LIMIT"
		});
		this.name = "ListLimitError";
		this.maxLimit = maxLimit;
		Object.setPrototypeOf(this, ListLimitError.prototype);
	}
};
/**
* Resolve a client-supplied list `limit` into a safe, always-defined value.
*
* - An absent / blank limit falls back to the mode default:
*   `vectorDefaultLimit` for a vector search, otherwise `defaultLimit`.
* - A limit that is present must be an integer in `[1, maxLimit]`. Anything
*   else — `0`, a negative, `1.5`, `abc`, `100000000` — throws
*   {@link ListLimitError} rather than being coerced into range, because every
*   coercion answers a question the caller did not ask with a page it cannot
*   tell apart from the whole collection.
*
* The return is never `undefined` — no ingress that routes its client limit
* through this can produce an unbounded read.
*
* @throws {ListLimitError} when a present `limit` is not an integer in range.
*/
function resolveClientListLimit(rawLimit, opts = {}) {
	const maxLimit = opts.maxLimit ?? 1e3;
	if (rawLimit != null && String(rawLimit).trim() !== "") {
		const parsed = typeof rawLimit === "number" ? rawLimit : Number(String(rawLimit).trim());
		if (!Number.isInteger(parsed) || parsed < 1) throw new ListLimitError(`Invalid \`limit\`: ${String(rawLimit)}. Expected a whole number between 1 and ${maxLimit}.`, maxLimit);
		if (parsed > maxLimit) throw new ListLimitError(`\`limit\` ${parsed} is above the maximum of ${maxLimit}. Ask for at most ${maxLimit} rows per read and page through the rest with \`offset\` — answering with a smaller page would be indistinguishable from there being no more rows.`, maxLimit);
		return parsed;
	}
	return opts.vectorSearch ? opts.vectorDefaultLimit ?? 10 : opts.defaultLimit ?? 50;
}
//#endregion
//#region src/controllers/storage.ts
/**
* Path prefix that marks an object as **public**. Files stored under this
* prefix are served without any auth token via a stable, permanent,
* CDN-cacheable URL (see {@link StorageSource.getSignedUrl}). Shared by the
* client SDK and the backend so both agree on which objects are public.
*
* @group Models
*/
var PUBLIC_STORAGE_PREFIX = "public/";
/**
* True when a storage key/path points at a public object (lives under
* {@link PUBLIC_STORAGE_PREFIX}). The check is applied to the key *within the
* bucket* — strip any `bucket/` and `scheme://` prefixes first.
*
* @group Models
*/
function isPublicStoragePath(path) {
	if (!path) return false;
	let p = path;
	const scheme = p.indexOf("://");
	if (scheme !== -1) p = p.substring(scheme + 3);
	p = p.replace(/^\/+/, "");
	if (p.split("/").some((seg) => seg === "..")) return false;
	return p.startsWith("public/") || p.startsWith(`default/public/`);
}
//#endregion
export { ADMIN_COLLECTION_KEYS, ADMIN_PROPERTY_KEYS, ALL_WHERE_FILTER_OPS, ANONYMOUS_USER_ID, ANONYMOUS_USER_IDS, BATCH_REF_KEY, BUNDLE_FORMAT_VERSION, CANONICAL_TO_REST, DEFAULT_CAPABILITIES, DEFAULT_COMMIT_PATHS, DEFAULT_DATA_SOURCE_KEY, DEFAULT_FILTERABLE_RELATION_KINDS, DEFAULT_FUZZY_THRESHOLD, DEFAULT_LIST_LIMIT, DEFAULT_RESOURCE_KEY, DEFAULT_SEARCH_COLUMN, DEFAULT_SEARCH_LANGUAGE, DEFAULT_SEARCH_MODE, DEFAULT_SEARCH_WEIGHT, DEFAULT_STORAGE_SOURCE_KEY, DEFAULT_TENANT_BYPASS_ROLES, DEFAULT_VECTOR_LIST_LIMIT, EntityReference, EntityRelation, FIELD_OPERATORS, FIREBASE_CAPABILITIES, GeoPoint, JUNCTION_PIVOT_KEY, LEGACY_RLS_JWT_SQL, LEGACY_RLS_ROLES_SQL, LEGACY_RLS_SCHEMA, LEGACY_RLS_UID_SQL, LIST_OPS, ListLimitError, MAX_INCLUDE_DEPTH, MAX_LIST_LIMIT, MONGODB_CAPABILITIES, NULL_OPS, POSTGRES_CAPABILITIES, PUBLIC_STORAGE_PREFIX, REBASE_SCHEMA, RELEVANCE_SORT_FIELD, RESERVED_BACKEND_PREFIXES, RESOURCE_GRAPH_VERSION, REST_TO_CANONICAL, RLS_IS_ANONYMOUS_SQL, RLS_JWT_SQL, RLS_ROLES_SQL, RLS_UID_SQL, RUNTIME_CONTRACT_VERSION, RebaseApiError, RebaseClientError, SCHEMA_VERSION_HEADER, Vector, amendResourceKind, bucket, buildResourceGraph, canonicalSchemaPayload, computeSchemaVersion, database, declareCron, declareFunction, declareResource, declaredDataSources, declaredDatabaseExtensions, declaredQueueConsumers, declaredResources, declaredStorageSources, declaredSubscriptions, deserializeCollections, encodeRelationAggregateSort, envBasesForResource, findEnvSuffixCollision, findStorageSuffixCollision, getCollectionDataPath, getDataSourceCapabilities, getDeclaredSubcollections, hasFieldOperation, hasForeignKeyOnTarget, isAnonymousUid, isBranchAdmin, isChannelBusInstance, isDocumentAdmin, isFieldOperation, isFirebaseCollectionConfig, isLazyComponentRef, isManyToMany, isMongoDBCollectionConfig, isPostgresCollectionConfig, isPublicStoragePath, isRelationAggregateSort, isRelationalCollectionConfig, isResourceHandle, isSQLAdmin, isSchemaAdmin, isSchemaEditingAdmin, isSerializedCollectionRef, isTenantClaimSource, isTenantMembershipSource, isToMany, isUnsupported, isValidEngine, nestAdminCollectionKeys, nestAdminKeysOf, nestAdminPropertyKeys, parseEnvBoolean, parseRelationAggregateSort, policy, queue, registerDataSourceCapabilities, registerResourceKind, reservedPrefixFor, resetDeclaredQueueConsumers, resetDeclaredResources, resetDeclaredSubscriptions, resolveClientListLimit, resolveResourceRefs, resourceEnvSuffix, resourceId, resourceKeyOf, resourceKind, resourceKinds, resourceToDataSource, resourceToStorageSource, rewriteLegacyRlsFunctions, serializeCollections, setQueueRuntime, setTopicRuntime, sortKeyToString, storageEnvSuffix, toCanonicalOp, topic, unsupportedMethod, usesLegacyRlsFunctions };

//# sourceMappingURL=index.es.js.map